import { TicketsEmptyState } from "@jugl-web/domain-resources/tickets/components/TicketsEmptyState";
import { TicketsLoadingErrorState } from "@jugl-web/domain-resources/tickets/components/TicketsLoadingErrorState";
import { TicketsNoResultsState } from "@jugl-web/domain-resources/tickets/components/TicketsNoResultsState";
import { TicketsTableSkeleton } from "@jugl-web/domain-resources/tickets/components/TicketsTableSkeleton";
import { useUpdateTicketOptimistically } from "@jugl-web/domain-resources/tickets/hooks/useUpdateTicketOptimistically";
import { UpdateTicketPayload } from "@jugl-web/rest-api";
import { Button, DelayedRender, LoadingAnimation } from "@jugl-web/ui-kit";
import { useTranslations } from "@jugl-web/utils";
import { useMe } from "@web-src/modules/auth/hooks/useMe";
import { TicketCreationBlockedTooltip } from "@web-src/modules/client-support/components/TicketCreationBlockedTooltip";
import { useTicketsLimit } from "@web-src/modules/client-support/hooks/useTicketsLimit";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import { useInView } from "react-intersection-observer";
import { useClientSupportPageContext } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { useTicketsViewContext } from "../../../../providers/TicketsViewProvider";
import {
  ClientSupportTicketsView,
  NewTicketFormValues,
} from "../../../../types";
import { TicketsViewTable } from "../TicketsViewTable";
import { ReactComponent as PlusIcon } from "./assets/plus.svg";
import { onBeforeRefresh$ } from "../TicketsViewTopBar/components/TicketsViewRefreshButton";

interface TicketsViewContentProps {
  ticketsView: ClientSupportTicketsView;
}

export const TicketsViewContent: FC<TicketsViewContentProps> = ({
  ticketsView,
}) => {
  const { entityId } = useEntitySelectedProvider();
  const { id: meId = "" } = useMe();

  const { openCreateTicketSidebar } = useClientSupportPageContext();
  const { ticketListQuery, searchQuery, sort } = useTicketsViewContext();
  const { isTicketCreationBlocked } = useTicketsLimit();
  const { t } = useTranslations();

  const {
    tickets,
    hasMore,
    isLoading,
    isLoadingMore,
    isError,
    loadMore,
    retry,
    setTickets,
  } = ticketListQuery;

  const { updateTicket } = useUpdateTicketOptimistically({
    entityId,
    setTickets,
  });

  const { ref: sentinelRef, inView } = useInView();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleOpenPrefilledNewTicketForm = useCallback(() => {
    let initialFormValues: Partial<NewTicketFormValues> = {};

    switch (ticketsView.filter) {
      case "assigned_to_user":
        initialFormValues = { assigneeIds: [meId] };
        break;
      case "in_progress":
      case "on_customer":
      case "on_hold":
      case "issued":
        initialFormValues = { status: ticketsView.filter };
        break;
      default:
        break;
    }

    openCreateTicketSidebar(initialFormValues);
  }, [ticketsView.filter, meId, openCreateTicketSidebar]);

  const produceUpdateTicketFn = useCallback(
    (ticketId: string) => (data: UpdateTicketPayload) =>
      updateTicket(ticketId, data),
    [updateTicket]
  );

  const createAction = useMemo(() => {
    const createTicketLabel = t({
      id: "client-support-page.create-ticket",
      defaultMessage: "Create Ticket",
    });

    if (isTicketCreationBlocked) {
      return (
        <TicketCreationBlockedTooltip
          renderTrigger={({ props, ref }) => (
            <div ref={ref} {...props}>
              <Button
                variant="primary"
                size="sm"
                isDisabled
                iconEnd={<PlusIcon />}
              >
                {createTicketLabel}
              </Button>
            </div>
          )}
        />
      );
    }

    return (
      <Button
        variant="primary"
        size="sm"
        iconEnd={<PlusIcon />}
        onClick={handleOpenPrefilledNewTicketForm}
      >
        {createTicketLabel}
      </Button>
    );
  }, [handleOpenPrefilledNewTicketForm, isTicketCreationBlocked, t]);

  // Load more tickets when the sentinel is in view
  useEffect(() => {
    if (inView && hasMore && !isLoadingMore) {
      loadMore();
    }
  }, [inView, hasMore, isLoadingMore, loadMore]);

  // Reset scroll position when list query parameters change.
  useLayoutEffect(() => {
    scrollContainerRef.current?.scrollTo({ top: 0 });
  }, [ticketsView.filter, searchQuery, sort]);

  // Reset scroll position before refreshing the list
  useEffect(() => {
    onBeforeRefresh$.subscribe(() => {
      scrollContainerRef.current?.scrollTo({ top: 0 });
    });
  }, []);

  if (isLoading) {
    return (
      <DelayedRender
        isLoading
        loadingContent={
          <div className="flex h-full w-full animate-fade-in flex-col">
            <TicketsTableSkeleton />
          </div>
        }
      />
    );
  }

  if (isError) {
    return (
      <div className="flex h-full animate-fade-in items-center justify-center">
        <TicketsLoadingErrorState onRetry={retry} />
      </div>
    );
  }

  const isEmpty = tickets.length === 0;

  if (isEmpty) {
    return (
      <div
        key="empty-state"
        className="flex h-full animate-fade-in items-center justify-center"
      >
        {searchQuery.length > 0 ? (
          <TicketsNoResultsState />
        ) : (
          <TicketsEmptyState
            variant={ticketsView.filter}
            createAction={createAction}
          />
        )}
      </div>
    );
  }

  return (
    <div
      ref={scrollContainerRef}
      className="jugl__custom-scrollbar flex h-full w-full animate-fade-in flex-col overflow-auto"
    >
      <TicketsViewTable
        entityId={entityId}
        tickets={tickets}
        produceUpdateTicketFn={produceUpdateTicketFn}
      />

      {hasMore && !isLoadingMore && (
        <div
          ref={sentinelRef}
          className="flex items-center justify-center pt-2 pb-4"
        >
          <LoadingAnimation size="lg" />
        </div>
      )}
    </div>
  );
};
